/*
    Vanilla replacements for the Iguana getters on Lesson, Lesson.FrameList and the
    IsContentItem mixin that the stream dashboard reads. Each function here has a spec asserting
    it returns the same value as the getter it replaces.

    These expect lessonProgress on the lesson. useStreamWithLiveProgress and adaptStoredStreamForDashboard
    add it.
*/
import { type LessonForStreamDashboard, type StreamForStreamDashboard } from 'Lessons';
import { type KeyTermForDisplay, type ProgressStatusName } from '../StreamDashboard.types';

export const isLessonComplete = (lesson: LessonForStreamDashboard) => !!lesson.lessonProgress?.complete;

export const isLessonStarted = (lesson: LessonForStreamDashboard) => !!lesson.lessonProgress?.startedAt;

/** Mirrors Lesson#progressStatus, including LessonProgress#inProgress. */
export const getLessonProgressStatusName = (lesson: LessonForStreamDashboard): ProgressStatusName => {
    const progress = lesson.lessonProgress;
    if (progress) {
        if (!!progress.startedAt && !progress.complete) return 'in_progress';
        if (progress.complete) return 'completed';
    }
    return 'not_started';
};

/** Mirrors Lesson#lastProgressAt, which returns a Date built from a seconds timestamp. */
export const getLessonLastProgressAt = (lesson: LessonForStreamDashboard): Date | undefined => {
    const lastProgressAt = lesson.lessonProgress?.lastProgressAt;
    if (!lastProgressAt) return undefined;
    return new Date(1000 * lastProgressAt);
};

/** Mirrors Lesson#bestScore, which is undefined rather than null when there is no numeric score. */
export const getLessonBestScore = (lesson: LessonForStreamDashboard): number | undefined => {
    const bestScore = lesson.lessonProgress?.bestScore;
    return Number.isFinite(bestScore) ? (bestScore as number) : undefined;
};

/**
 * Mirrors the IsContentItem `launchText` getter. Returns a locale key rather than a translated
 * string, so that callers translate through react-i18next instead of TranslationHelper.
 */
export const getLessonLaunchTextKey = (lesson: LessonForStreamDashboard) => {
    if (isLessonComplete(lesson)) return 'review';
    if (isLessonStarted(lesson)) return 'resume';
    return 'start';
};

/**
 * Mirrors Lesson#getKeyTermsForDisplay.
 *
 * `stripFormatting` is injected because the only implementation is the Angular `FormatsText`
 * service, which handles MathJax and markdown and is used widely elsewhere. Passing it in keeps
 * this selector pure and testable instead of hiding an injector call inside it.
 */
export const getKeyTermsForDisplay = (
    lesson: LessonForStreamDashboard,
    stripFormatting: (text: string) => string,
): KeyTermForDisplay[] => {
    const definitions = lesson.keyTermDefinitions ?? [];

    return (lesson.keyTerms ?? []).map(keyTerm => {
        const definition = definitions.find(def => def.keyTerm === keyTerm);
        const stripped = stripFormatting(keyTerm);
        return {
            keyTerm: stripped.substring(0, 1).toUpperCase() + stripped.substring(1),
            rawKeyTerm: keyTerm,
            definition: definition?.definition ?? undefined,
            verified: definition?.verified ?? false,
        };
    });
};

/*
    Mirrors showAssessmentArc. The Iguana waiver check was always true, so a complete assessment with no
    score shows a badge, not a 0% arc.
*/
export const shouldShowAssessmentArc = (
    stream: StreamForStreamDashboard,
    lesson: LessonForStreamDashboard,
    { hideInProgress = false }: { hideInProgress?: boolean } = {},
) => {
    const hasNoBestScore = getLessonBestScore(lesson) == null;

    if (lesson.assessment && isLessonComplete(lesson) && hasNoBestScore) return false;
    if (hideInProgress && lesson.assessment && !isLessonComplete(lesson)) return false;

    return (
        (lesson.assessment && (isLessonComplete(lesson) || !isLessonStarted(lesson))) ||
        (stream.exam && !!stream.lessonStreamsProgress?.complete)
    );
};
